import React from 'react'
import { Box, Button, CircularProgress, Modal, Typography } from '@mui/material'

export type ConfirmActionModalConfirmColor = 'danger' | 'primary'

interface ConfirmActionModalProps {
  open: boolean
  title: string
  message: string
  confirmLabel: string
  onClose: () => void
  onConfirm: () => void
  confirmColor?: ConfirmActionModalConfirmColor
  loading?: boolean
}

const ConfirmActionModal = ({
  open,
  title,
  message,
  confirmLabel,
  onClose,
  onConfirm,
  confirmColor = 'danger',
  loading = false,
}: ConfirmActionModalProps): JSX.Element => {
  const confirmBg = confirmColor === 'danger' ? '#DC2626' : '#007C91'
  const confirmHoverBg = confirmColor === 'danger' ? '#B91C1C' : '#006B7D'

  return (
    <Modal
      open={open}
      onClose={loading ? undefined : onClose}
      disableAutoFocus
      sx={{ zIndex: 10000 }}
    >
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}
      >
        <Box
          onClick={loading ? undefined : onClose}
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(4px)',
            cursor: loading ? 'default' : 'pointer',
          }}
        />
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            bgcolor: 'white',
            borderRadius: 3,
            p: 3,
            maxWidth: 448,
            width: '100%',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontSize: '1.25rem',
              fontWeight: 600,
              color: '#1F2937',
              textAlign: 'center',
              mb: 2,
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontSize: '1rem',
              color: '#374151',
              textAlign: 'center',
              mb: 3,
            }}
          >
            {message}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              fullWidth
              onClick={onClose}
              disabled={loading}
              sx={{
                flex: 1,
                py: 1,
                bgcolor: '#E5E7EB',
                color: '#374151',
                fontWeight: 500,
                textTransform: 'none',
                borderRadius: 1.5,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#D1D5DB', boxShadow: 'none' },
                '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' },
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              onClick={onConfirm}
              disabled={loading}
              sx={{
                flex: 1,
                py: 1,
                bgcolor: confirmBg,
                color: 'white',
                fontWeight: 500,
                textTransform: 'none',
                borderRadius: 1.5,
                boxShadow: 'none',
                '&:hover': { bgcolor: confirmHoverBg, boxShadow: 'none' },
                '&.Mui-disabled': { bgcolor: confirmBg, color: 'white', opacity: 0.7 },
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : confirmLabel}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}

export default ConfirmActionModal
